import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayLayer, useUiLabels } from '../ThemeProvider.js';
import { Icon } from './Icon.js';
const ToastContext = createContext(null);
/** How long a toast stays, by tone. Errors stay longer: they usually need reading twice. */
const DURATION = { success: 4000, info: 5000, error: 8000 };
const LIMIT = 3;
/**
 * Feedback for something that happened, without taking the page away.
 *
 * Two live regions, not one: a success is `polite` (said when the reader
 * pauses), a failure is `assertive` (said now). Both regions exist from the
 * first render, because a live region created at the moment of the message is
 * often not announced at all. Timers are cleared on unmount and when a toast
 * is dismissed by hand.
 */
export function ToastProvider({ children }) {
    const [items, setItems] = useState([]);
    const timers = useRef(new Map());
    const nextId = useRef(1);
    const layer = useOverlayLayer();
    const labels = useUiLabels();
    const dismiss = useCallback((id) => {
        const timer = timers.current.get(id);
        if (timer !== undefined) {
            clearTimeout(timer);
            timers.current.delete(id);
        }
        setItems((current) => current.filter((item) => item.id !== id));
    }, []);
    const push = useCallback((tone, message) => {
        const id = nextId.current++;
        setItems((current) => [...current, { id, tone, message }].slice(-LIMIT));
        timers.current.set(id, setTimeout(() => {
            dismiss(id);
        }, DURATION[tone]));
    }, [dismiss]);
    useEffect(() => {
        const pending = timers.current;
        return () => {
            for (const timer of pending.values()) {
                clearTimeout(timer);
            }
            pending.clear();
        };
    }, []);
    const api = useMemo(() => ({
        success: (message) => {
            push('success', message);
        },
        error: (message) => {
            push('error', message);
        },
        info: (message) => {
            push('info', message);
        },
    }), [push]);
    const region = (politeness, tones) => (_jsx("div", { className: "lh-toasts__region", "aria-live": politeness, "aria-atomic": "false", "data-politeness": politeness, children: items
            .filter((item) => tones.includes(item.tone))
            .map((item) => (_jsxs("div", { className: `lh-toast lh-toast--${item.tone}`, children: [_jsx("span", { className: "lh-toast__icon", "aria-hidden": "true", children: _jsx(Icon, { name: item.tone === 'error' ? 'alert' : item.tone === 'success' ? 'check' : 'info', size: 18 }) }), _jsx("span", { className: "lh-toast__message", children: item.message }), _jsx("button", { type: "button", className: "lh-toast__dismiss", "aria-label": labels.dismiss, onClick: () => {
                        dismiss(item.id);
                    }, children: _jsx(Icon, { name: "close", size: 16 }) })] }, item.id))) }));
    const toasts = (_jsxs("div", { className: "lh-toasts", children: [region('assertive', ['error']), region('polite', ['success', 'info'])] }));
    return (_jsxs(ToastContext, { value: api, children: [children, layer === null ? null : layer ? createPortal(toasts, layer) : toasts] }));
}
/**
 * The toast API. Outside a provider it does nothing rather than throwing, so a
 * component reused in a test or a page without toasts still renders.
 */
export function useToast() {
    return use(ToastContext) ?? NOOP;
}
const NOOP = {
    success: () => undefined,
    error: () => undefined,
    info: () => undefined,
};
//# sourceMappingURL=Toast.js.map